import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KpiTile } from "@/components/KpiTile";
import { DataTable } from "@/components/DataTable";
import { Shimmer } from "@/components/Shimmer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { fmtCurrency, fmtNum, fmtBytes, fmtSeconds, fmtCost } from "@/lib/format";

const STAGE_COLORS = ["#09090B", "#3B82F6", "#10B981", "#F59E0B", "#F43F5E", "#8B5CF6", "#14B8A6"];
const MIME_COLORS  = ["#09090B", "#52525B", "#A1A1AA", "#D4D4D8"];

export default function ValuePage() {
  const [window_, setWindow] = useState("this_quarter");
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/value", { params: { window: window_ } }).then((r) => setData(r.data));
  }, [window_]);

  const emailsHref = (extra = {}) => {
    const p = new URLSearchParams();
    if (data?.window_start) p.set("date_from", data.window_start);
    if (data?.window_end)   p.set("date_to",   data.window_end);
    Object.entries(extra).forEach(([k, v]) => v != null && p.set(k, String(v)));
    return `/emails?${p.toString()}`;
  };

  // Shape weekly into stacked rows { week, [stage]: amount }
  const weeklyStacked = React.useMemo(() => {
    if (!data) return { rows: [], stages: [] };
    const byWeek = {};
    const stages = new Set();
    data.weekly_pipeline.forEach(({ week, stage, amount }) => {
      if (!byWeek[week]) byWeek[week] = { week };
      byWeek[week][stage] = (byWeek[week][stage] || 0) + amount;
      stages.add(stage);
    });
    return { rows: Object.values(byWeek).sort((a, b) => a.week.localeCompare(b.week)), stages: [...stages] };
  }, [data]);

  const llmByNodeCols = useMemo(() => [
    { accessorKey: "node", header: "Node", cell: ({ getValue }) => <span className="capitalize strong">{getValue()}</span> },
    { accessorKey: "invocations", header: "Calls", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
    { accessorKey: "cost_usd", header: "Cost", meta: { align: "right" }, cell: ({ getValue }) => <span className="num strong">{fmtCost(getValue())}</span> },
  ], []);

  const llmByAccountCols = useMemo(() => [
    {
      accessorKey: "account", header: "Account", size: 320,
      cell: ({ getValue }) => <span className="truncate strong block">{getValue()}</span>,
    },
    { accessorKey: "email_count", header: "Emails", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
    { accessorKey: "opportunity_count", header: "Opps", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
    { accessorKey: "tokens", header: "Tokens", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{fmtNum(getValue())}</span> },
    { accessorKey: "llm_cost_usd", header: "LLM cost", meta: { align: "right" }, cell: ({ getValue }) => <span className="num strong">{fmtCost(getValue())}</span> },
    { accessorKey: "max_deal_amount", header: "Max deal $", meta: { align: "right" }, cell: ({ getValue }) => <span className="num">{fmtCurrency(getValue(), true)}</span> },
    {
      id: "ratio", header: "Cost / $1K deal", meta: { align: "right" }, enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        const ratio = r.max_deal_amount ? (r.llm_cost_usd / (r.max_deal_amount / 1000)) : null;
        return <span className="num" style={{ color: "#047857" }}>{ratio !== null ? fmtCost(ratio) : "—"}</span>;
      },
    },
  ], []);

  return (
    <div className="p-8 max-w-[1700px]" data-testid="value-page">
      <PageHeader
        eyebrow="Executive view"
        title="Business Value"
        subtitle="What the Gmail → Salesforce agent delivered, and how fast."
        actions={
          <Select value={window_} onValueChange={setWindow}>
            <SelectTrigger className="w-[180px]" data-testid="value-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_quarter">This quarter</SelectItem>
              <SelectItem value="last_quarter">Last quarter</SelectItem>
              <SelectItem value="ytd">Year to date</SelectItem>
              <SelectItem value="last_30d">Last 30 days</SelectItem>
              <SelectItem value="last_7d">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* ROI HERO — the headline number */}
      {data && data.kpis.roi_multiplier !== null && data.kpis.roi_multiplier !== undefined && (
        <section
          className="relative overflow-hidden mb-6 rounded-md bg-zinc-950 text-zinc-100 p-6 lg:p-8 flex flex-wrap items-end gap-8"
          data-testid="roi-hero"
        >
          <div className="absolute inset-0 opacity-[0.05] grid-lines pointer-events-none" />
          <div className="relative">
            <div className="overline text-emerald-400/80 mb-2">Return on investment · {window_.replace(/_/g, " ")}</div>
            <div className="font-serif text-5xl sm:text-6xl leading-none tracking-tight">
              {Math.round(data.kpis.roi_multiplier).toLocaleString()}<span className="text-zinc-400">×</span>
            </div>
            <div className="mt-2 font-serif italic text-zinc-400 text-sm sm:text-base max-w-lg leading-snug">
              Every dollar spent on LLM calls returned {Math.round(data.kpis.roi_multiplier).toLocaleString()}&times; in manual-review effort saved.
            </div>
          </div>
          <div className="relative flex flex-wrap gap-6 text-sm font-mono ml-auto">
            <HeroStat label="Manual work saved"  value={fmtCurrency(data.kpis.manual_cost_saved_usd)} />
            <HeroStat label="LLM spend"          value={fmtCost(data.kpis.llm_cost_total_usd)} />
            <HeroStat label="Net savings"        value={fmtCurrency(data.kpis.net_savings_usd)} pos />
          </div>
          <div className="relative w-full pt-4 border-t border-zinc-800 text-[11px] text-zinc-500 font-mono flex flex-wrap gap-x-6 gap-y-1">
            <span>{data.kpis.opportunities_created || 0} opportunities</span>
            <span>× {data.kpis.minutes_per_opp} min saved/opp</span>
            <span>× ${data.kpis.loaded_hourly_rate_usd}/hr loaded rate</span>
            <span className="text-zinc-400">= {fmtCurrency(data.kpis.manual_cost_saved_usd)}</span>
            <span className="ml-auto">Adjust the rate in Settings →</span>
          </div>
        </section>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="value-kpis">
        {data ? (
          <>
            <KpiTile testId="kpi-opps"   label="Opportunities"    value={fmtNum(data.kpis.opportunities_created)} delta={data.kpis.deltas.opportunities_created_pct} />
            <KpiTile testId="kpi-total"  label="Total $ amount"   value={fmtCurrency(data.kpis.total_amount, true)} delta={data.kpis.deltas.total_amount_pct} />
            <KpiTile testId="kpi-weight" label="Weighted pipeline" value={fmtCurrency(data.kpis.weighted_pipeline, true)} delta={data.kpis.deltas.weighted_pipeline_pct} />
            <KpiTile testId="kpi-median" label="Median time-to-SF" value={fmtSeconds(data.kpis.median_time_to_sf_seconds)} />
            <KpiTile testId="kpi-att"    label="Attachments"      value={fmtNum(data.kpis.attachments_processed)} suffix={fmtBytes(data.kpis.attachments_bytes)} />
            <KpiTile testId="kpi-saved"  label="Manual min saved" value={fmtNum(data.kpis.manual_effort_minutes_saved)} suffix="min" />
          </>
        ) : [...Array(6)].map((_, i) => <Shimmer key={i} className="h-[120px]" />)}
      </div>

      {/* LLM cost row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="value-llm-kpis">
        {data ? (
          <>
            <KpiTile testId="kpi-llm-total"    label="LLM spend"        value={fmtCost(data.kpis.llm_cost_total_usd)} delta={data.kpis.deltas.llm_cost_pct} />
            <KpiTile testId="kpi-llm-per-opp"  label="Cost per opp"     value={fmtCost(data.kpis.llm_cost_per_opportunity_usd)} />
            <KpiTile testId="kpi-llm-per-mail" label="Cost per email"   value={fmtCost(data.kpis.llm_avg_cost_per_email_usd)} />
            <KpiTile testId="kpi-llm-in"       label="Input tokens"     value={fmtNum(data.kpis.llm_input_tokens)} />
            <KpiTile testId="kpi-llm-out"      label="Output tokens"    value={fmtNum(data.kpis.llm_output_tokens)} />
          </>
        ) : [...Array(5)].map((_, i) => <Shimmer key={i} className="h-[120px]" />)}
      </div>

      {/* Weekly + Funnel row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <section className="card-surface p-4 lg:col-span-2" data-testid="weekly-chart">
          <div className="overline mb-3">Weekly pipeline $ · stacked by stage</div>
          <div className="h-[260px]">
            {data && weeklyStacked.rows.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={weeklyStacked.rows}>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                  <YAxis tickFormatter={(v) => fmtCurrency(v, true)} tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ fontSize: 12, fontFamily: "Geist Mono" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {weeklyStacked.stages.map((s, i) => (
                    <Bar key={s} dataKey={s} stackId="a" fill={STAGE_COLORS[i % STAGE_COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">No pipeline activity in this window</div>
            )}
          </div>
        </section>

        <section className="card-surface p-4" data-testid="funnel-card">
          <div className="overline mb-4">Conversion funnel</div>
          {data && (
            <Funnel funnel={data.funnel} emailsHref={emailsHref} nav={nav} />
          )}
        </section>
      </div>

      {/* Top accounts + Attachment mix row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <section className="card-surface p-4 lg:col-span-2" data-testid="top-accounts">
          <div className="overline mb-3">Top accounts by pipeline value · click a bar to drill down</div>
          <div className="h-[260px]">
            {data && data.top_accounts.length > 0 ? (
              <ResponsiveContainer>
                <BarChart layout="vertical" data={data.top_accounts}
                          onClick={(e) => {
                            const name = e?.activePayload?.[0]?.payload?.account;
                            if (name) nav(emailsHref({ company: name }));
                          }}>
                  <CartesianGrid stroke="#F4F4F5" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => fmtCurrency(v, true)} tick={{ fontSize: 11, fontFamily: "Geist Mono" }} />
                  <YAxis type="category" dataKey="account" width={180} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ fontSize: 13 }} />
                  <Bar dataKey="amount" fill="#09090B" barSize={16} cursor="pointer" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm meta">No data</div>
            )}
          </div>
        </section>

        <section className="card-surface p-4" data-testid="attachment-mix">
          <div className="overline mb-3">Attachment mix</div>
          <div className="h-[260px]">
            {data && data.attachment_mix.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.attachment_mix} dataKey="count" nameKey="mime_type" innerRadius={50} outerRadius={90} label={{ fontSize: 10, fontFamily: "Geist Mono" }}>
                    {data.attachment_mix.map((_, i) => (
                      <Cell key={i} fill={MIME_COLORS[i % MIME_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">No attachments</div>
            )}
          </div>
        </section>
      </div>

      {/* Latency histogram */}
      <section className="card-surface p-4 mb-6" data-testid="latency-hist">
        <div className="overline mb-3">Latency distribution · received → Salesforce</div>
        <div className="h-[200px]">
          {data ? (
            <ResponsiveContainer>
              <BarChart data={data.latency_histogram}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                <YAxis tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill="#09090B" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Shimmer className="h-full" />}
        </div>
      </section>

      {/* LLM spend row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <section className="card-surface p-4 lg:col-span-2" data-testid="llm-weekly-chart">
          <div className="overline mb-3">Weekly LLM spend · $ & tokens</div>
          <div className="h-[240px]">
            {data && data.llm_weekly.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={data.llm_weekly}>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                  <YAxis yAxisId="cost" tickFormatter={(v) => fmtCost(v)} tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                  <YAxis yAxisId="tokens" orientation="right" tickFormatter={(v) => fmtNum(v)} tick={{ fontSize: 10, fontFamily: "Geist Mono" }} />
                  <Tooltip contentStyle={{ fontSize: 12, fontFamily: "Geist Mono" }} formatter={(v, n) => n === "cost_usd" ? fmtCost(v) : fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="cost" dataKey="cost_usd" fill="#09090B" name="cost" />
                  <Bar yAxisId="tokens" dataKey="tokens" fill="#A1A1AA" name="tokens" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">No LLM spend in window</div>
            )}
          </div>
        </section>

        <DataTable
          data-testid="llm-by-node-card"
          columns={llmByNodeCols}
          data={data?.llm_by_node || []}
          emptyMessage="No LLM calls"
          maxHeight="none"
          header={<div className="overline">Spend by node</div>}
        />
      </div>

      {/* LLM cost per account — correlates LLM spend with deal size */}
      <div className="mb-6">
        <DataTable
          data-testid="llm-by-account"
          columns={llmByAccountCols}
          data={data?.llm_by_account || []}
          onRowClick={(r) => nav(emailsHref({ company: r.account }))}
          getRowTestId={(r) => `llm-acct-${r.account}`}
          emptyMessage="No account-level LLM data"
          maxHeight="none"
          header={
            <>
              <div className="overline">LLM cost per account · top 10</div>
              <span className="mono text-[11px] meta">click a row to filter emails by account</span>
            </>
          }
        />
      </div>
    </div>
  );
}

function HeroStat({ label, value, pos }) {
  return (
    <div>
      <div className="overline text-zinc-500">{label}</div>
      <div className={`text-2xl mt-1 ${pos ? "text-emerald-400" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function Funnel({ funnel, emailsHref, nav }) {
  const SALES_CATS = ["rfq","quote","sales","lead","inquiry"];
  const steps = [
    { label: "Emails received",  value: funnel.emails_received,       href: emailsHref ? emailsHref() : null },
    { label: "Sales-relevant",   value: funnel.sales_relevant,        href: null /* backend ILIKE any of cats */ },
    { label: "Opportunities",    value: funnel.opportunities_created, href: "/opportunities" },
    { label: "Closed won",       value: funnel.closed_won,            href: null },
  ];
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].value : null;
        const drop = prev && prev > 0 ? (1 - s.value / prev) * 100 : null;
        const body = (
          <>
            <div className="flex items-baseline justify-between text-sm mb-1">
              <span className="meta">{s.label}</span>
              <span>
                <b className="text-zinc-900">{fmtNum(s.value)}</b>
                {drop !== null && drop > 0 && <span className="text-rose-600 ml-2 mono text-xs">−{drop.toFixed(0)}%</span>}
              </span>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-zinc-900" style={{ width: `${(s.value / max) * 100}%` }} />
            </div>
          </>
        );
        if (s.href) {
          return (
            <li
              key={s.label}
              data-testid={`funnel-step-${i}`}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => nav && nav(s.href)}
            >
              {body}
            </li>
          );
        }
        return <li key={s.label} data-testid={`funnel-step-${i}`}>{body}</li>;
      })}
      <li className="text-[11px] meta mono pt-2">Sales-relevant filters to: {SALES_CATS.join(", ")}</li>
    </ol>
  );
}
